<template>
  <div
    class="note-label"
    :class="[
      `note-label--${size}`,
      {
        'note-label--accidental': hasAccidental,
        'note-label--both': isBothMode,
      },
    ]"
  >
    <template v-if="isBothMode">
      <!-- Special handling for "both" mode with two note names -->
      <span class="note-both">{{ displayName }}</span>
    </template>
    <template v-else>
      <!-- Normal single note display -->
      <span class="note-letter">{{ noteLetter }}</span>
      <!-- Custom SVG accidental symbols -->
      <svg
        v-if="isSharp"
        class="note-accidental note-accidental--sharp"
        :width="getAccidentalSize().width"
        :height="getAccidentalSize().height"
        :viewBox="svgPaths.sharp.viewBox"
      >
        <path
          v-for="(path, index) in svgPaths.sharp.paths"
          :key="index"
          :d="path.d"
          fill="currentColor"
        />
      </svg>
      <svg
        v-if="isFlat"
        class="note-accidental note-accidental--flat"
        :width="getAccidentalSize().width"
        :height="getAccidentalSize().height"
        :viewBox="svgPaths.flat.viewBox"
      >
        <path
          v-for="(path, index) in svgPaths.flat.paths"
          :key="index"
          :d="path.d"
          fill="currentColor"
        />
      </svg>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, withDefaults, defineProps } from "vue";
import {
  getDisplayNoteName,
  parseNoteComponents,
  type AccidentalDisplayMode,
} from "@/utils/noteUtils";
import { svgPaths } from "@/utils/svgPaths";

interface Props {
  chromaticIndex: number;
  displayMode?: AccidentalDisplayMode;
  keySignature?: string;
  size?: "small" | "medium" | "large";
}

const props = withDefaults(defineProps<Props>(), {
  displayMode: "logical",
  size: "medium",
});

const displayName = computed(() => {
  return getDisplayNoteName(
    props.chromaticIndex,
    props.displayMode,
    props.keySignature
  );
});

const noteComponents = computed(() => {
  return parseNoteComponents(displayName.value);
});

const noteLetter = computed(() => {
  return noteComponents.value.letter;
});

const hasAccidental = computed(() => {
  return noteComponents.value.isSharp || noteComponents.value.isFlat;
});

const isSharp = computed(() => {
  return noteComponents.value.isSharp;
});

const isFlat = computed(() => {
  return noteComponents.value.isFlat;
});

const isBothMode = computed(() => {
  return props.displayMode === "both" && displayName.value.includes("/");
});

// Dynamic sizing for SVG accidental symbols based on component size
const getAccidentalSize = computed(() => () => {
  switch (props.size) {
    case "small":
      return { width: 7, height: 13 }; // ~70% of original
    case "large":
      return { width: 9, height: 17 }; // ~130% of original
    case "medium":
    default:
      return { width: 7, height: 13 }; // Original size
  }
});
</script>

<style scoped lang="scss">
.note-label {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  font-family: "Jost", sans-serif;
  font-weight: 600;
  text-align: center;
  color: inherit; // Ensure color inheritance from parent

  // Size variants
  &--small {
    .note-letter {
      font-size: 14px;
      line-height: 1;
    }
    .note-accidental {
      margin-left: 1px;
      align-self: flex-start;
      margin-top: 2px; // Slight vertical adjustment for small size
    }
    .note-both {
      font-size: 14px;
      line-height: 1;
    }
  }

  &--medium {
    .note-letter {
      font-size: 18px;
      line-height: 1;
    }
    .note-accidental {
      margin-left: 1px;
      align-self: flex-start;
      margin-top: 2px; // Slight vertical adjustment for medium size
    }
    .note-both {
      font-size: 14px;
      line-height: 1;
    }
  }

  &--large {
    .note-letter {
      font-size: 24px;
      line-height: 1;
    }
    .note-accidental {
      margin-left: 3px;
      align-self: flex-start;
      margin-top: -1px; // Slight vertical adjustment for large size
    }
    .note-both {
      font-size: 18px;
      line-height: 1;
    }
  }
}

.note-letter {
  font-family: "Jost", sans-serif;
  font-weight: 600;
  color: inherit;
}

.note-accidental {
  font-family: "Jost", sans-serif;
  font-weight: 400;
  position: relative;
  top: -0.1em; // Slight vertical adjustment for better alignment
  color: inherit; // Ensure SVG inherits color from parent

  svg {
    color: inherit; // Pass color to SVG elements
  }

  &--sharp {
    // Additional styling for sharp symbols if needed
  }

  &--flat {
    // Additional styling for flat symbols if needed
  }
}

.note-both {
  font-family: "Jost", sans-serif;
  font-weight: 600;
  white-space: nowrap;
}

// When displaying both sharp and flat (e.g., "C#/Db")
.note-label--both {
  // Additional styling for both mode if needed
}
</style>
